import { createClient } from '@supabase/supabase-js';

// Validate environment variables
const validateEnv = () => {
  const requiredEnvVars = {
    'REACT_APP_SUPABASE_URL': process.env.REACT_APP_SUPABASE_URL,
    'REACT_APP_SUPABASE_ANON_KEY': process.env.REACT_APP_SUPABASE_ANON_KEY,
  };

  const missingVars = Object.entries(requiredEnvVars)
    .filter(([_, value]) => !value)
    .map(([key]) => key);

  if (missingVars.length > 0) {
    throw new Error(`Missing required environment variables: ${missingVars.join(', ')}`);
  }

  // Validate Supabase URL format
  if (!process.env.REACT_APP_SUPABASE_URL.startsWith('https://')) {
    throw new Error('Supabase URL must use HTTPS');
  }
};

// Validate environment variables before creating client
validateEnv();

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Create Supabase client with enhanced security options
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Add session timeout (24 hours)
    sessionTimeout: 24 * 60 * 60,
  },
  // Storage specific settings
  storage: {
    url: `${supabaseUrl}/storage/v1`
  },
  // Global headers for security
  global: {
    headers: {
      'X-Content-Type-Options': 'nosniff',
      'X-Frame-Options': 'DENY',
      'X-XSS-Protection': '1; mode=block',
      'Referrer-Policy': 'strict-origin-when-cross-origin',
      'Permissions-Policy': 'camera=(), microphone=(), geolocation=()',
    },
  },
  // Database settings
  db: {
    schema: 'public',
  },
});

// Add storage URL for direct access (maintain backward compatibility)
supabase.storageUrl = `${supabaseUrl}/storage/v1`;

// Export a function to check if user is authenticated
export const isAuthenticated = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
};

// Export a function to get the current user
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// Role-based access control (RBAC)
export const hasRole = async (role) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  
  const { data, error } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', user.id)
    .single();
    
  if (error) return false;
  return data?.role === role;
};

// Check if user is admin
export const isAdmin = async () => {
  return hasRole('admin');
};

// Check if user has editor role
export const isEditor = async () => {
  return hasRole('editor');
};

// Export the client instance for backward compatibility
export default supabase; 